import { Injectable, signal, effect } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Inject } from '@angular/core';
import { TranslationService, Language } from './translation.service';

// Re-export Language type for convenience
export type { Language };

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly STORAGE_KEY = 'app-language';
  private readonly DEFAULT_LANGUAGE: Language = 'en';
  
  public readonly currentLanguage = signal<Language>(this.getStoredLanguage());
  public readonly isRTL = signal<boolean>(this.currentLanguage() === 'ar');

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private translationService: TranslationService
  ) {
    // Effect to update HTML attributes when language changes
    effect(() => {
      const lang = this.currentLanguage();
      const rtl = this.isRTL();
      
      this.document.documentElement.lang = lang;
      this.document.documentElement.dir = rtl ? 'rtl' : 'ltr';
      this.document.body.classList.toggle('rtl', rtl);
      this.document.body.classList.toggle('ltr', !rtl);
      
      // Store preference
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(this.STORAGE_KEY, lang);
      }

      // Load translations for the new language
      this.translationService.loadTranslations(lang).subscribe();
    });

    // Initialize with stored language
    const storedLang = this.getStoredLanguage();
    if (storedLang !== this.currentLanguage()) {
      this.setLanguage(storedLang);
    } else {
      // Load initial translations
      this.translationService.loadTranslations(storedLang).subscribe();
    }
  }

  setLanguage(lang: Language): void {
    this.currentLanguage.set(lang);
    this.isRTL.set(lang === 'ar');
  }

  private getStoredLanguage(): Language {
    if (typeof localStorage === 'undefined') {
      return this.DEFAULT_LANGUAGE;
    }
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return (stored === 'en' || stored === 'ar') ? stored : this.DEFAULT_LANGUAGE;
  }
}


